import { Calendar } from 'primereact/calendar';
import { useEffect, useMemo, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H5Emphasized,
    Icon,
    Input,
    PillButton,
    SearchIcon,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import type { MembershipDetails } from '../../entityTypes';
import { useMembershipHolisticSummary } from '../hooks/useMembershipHolisticSummary';
import { useSearchTransactions } from '../hooks/useSearchTransactions';
import { TransactionSearchResults } from './TransactionSearchResults';

interface TransactionSearchSectionProps {
    membershipDetails: MembershipDetails;
}

export type SortOption = 'relevance' | 'date-newest' | 'date-oldest' | 'amount';

export const TransactionSearchSection = ({
    membershipDetails,
}: TransactionSearchSectionProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [sortBy, setSortBy] = useState<SortOption>('relevance');

    const handleClearFilters = () => {
        setSearchQuery('');
        setDebouncedQuery('');
        setSelectedAccountId('');
        setStartDate(null);
        setEndDate(null);
        setSortBy('relevance');
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [searchQuery]);

    // Format dates for API (YYYY-MM-DD)
    const startDateString = startDate
        ? startDate.toISOString().split('T')[0]
        : undefined;
    const endDateString = endDate
        ? endDate.toISOString().split('T')[0]
        : undefined;

    const {
        data: searchResponse,
        isLoading,
        isError,
    } = useSearchTransactions(
        {
            membershipId: membershipDetails.id,
            q: debouncedQuery,
            limit: 50,
            accountId: selectedAccountId === '' ? undefined : selectedAccountId,
            startDate: startDateString,
            endDate: endDateString,
        },
        {
            enabled: debouncedQuery.length > 0,
        },
    );

    const { membershipHolisticSummary } = useMembershipHolisticSummary(
        membershipDetails.id,
    );

    // Flatten all accounts from all categories into a single array
    const allAccounts = useMemo(() => {
        if (membershipHolisticSummary.holisticSummary == null) {
            return [];
        }
        return Object.values(membershipHolisticSummary.holisticSummary).flatMap(
            (section) => section.accounts ?? [],
        );
    }, [membershipHolisticSummary.holisticSummary]);

    const hasActiveFilters =
        selectedAccountId !== '' ||
        startDate != null ||
        endDate != null ||
        sortBy !== 'relevance';

    return (
        <YStack
            borderRadius="$4"
            backgroundColor="$white"
            padding="$5"
            gap="$4"
            shadowColor="$dropletBlue800"
            shadowOffset={{ width: 0, height: 1 }}
            shadowOpacity={0.5}
            shadowRadius={4}
        >
            <YStack
                borderBottomWidth={1}
                borderColor="$gray200"
                paddingBottom="$2"
                marginBottom="$4"
            >
                <H5Emphasized>Transaction Search</H5Emphasized>
            </YStack>

            {/* Search Input and Account Filter - Shaded Background Block */}
            <YStack
                gap={12}
                padding={16}
                borderRadius={8}
                backgroundColor="$gray100"
            >
                <XStack width="100%" gap={12} alignItems="flex-start">
                    <YStack flex={1} gap={4}>
                        <BodySmallRegular
                            color="$gray1000"
                            fontWeight="emphasized"
                        >
                            Search term
                        </BodySmallRegular>
                        <YStack
                            padding={12}
                            borderRadius={8}
                            backgroundColor="$white"
                        >
                            <XStack gap={8} alignItems="center">
                                <Icon
                                    icon={SearchIcon}
                                    size={20}
                                    color="$gray1000"
                                />
                                <Input
                                    placeholder="Search transactions..."
                                    value={searchQuery}
                                    onChangeText={setSearchQuery}
                                    flex={1}
                                    padding={8}
                                    borderRadius={6}
                                    borderColor="$gray200"
                                    backgroundColor="$white"
                                    color="$black"
                                    fontWeight="regular"
                                    fontSize="$2"
                                    height={36}
                                />
                            </XStack>
                        </YStack>
                    </YStack>
                    <YStack flex={3} gap={4}>
                        <BodySmallRegular
                            color="$gray1000"
                            fontWeight="emphasized"
                        >
                            Account
                        </BodySmallRegular>
                        <YStack
                            padding={12}
                            borderRadius={8}
                            backgroundColor="$white"
                        >
                            <select
                                id="account-filter"
                                value={selectedAccountId}
                                onChange={(e) => {
                                    setSelectedAccountId(e.target.value);
                                }}
                                style={{
                                    padding: '8px',
                                    borderRadius: 6,
                                    borderWidth: 1,
                                    borderColor: '#e0e0e0',
                                    fontSize: 14,
                                    width: '100%',
                                    backgroundColor: 'white',
                                    height: '36px',
                                    lineHeight: '20px',
                                }}
                            >
                                <option value="">All</option>
                                {allAccounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                        {account.institutionName} -{' '}
                                        {account.name}
                                        {account.accountNumberMasked != null &&
                                        account.accountNumberMasked !== ''
                                            ? ` *${account.accountNumberMasked}`
                                            : ''}
                                    </option>
                                ))}
                            </select>
                        </YStack>
                    </YStack>
                </XStack>
            </YStack>
            {isLoading && debouncedQuery.length > 0 && (
                <XStack gap={8} alignItems="center">
                    <Spinner />
                    <BodyMediumRegular>Searching...</BodyMediumRegular>
                </XStack>
            )}
            {isError && (
                <BodyMediumRegular>
                    Error searching transactions. Please try again.
                </BodyMediumRegular>
            )}
            {!isLoading &&
                !isError &&
                debouncedQuery.length > 0 &&
                searchResponse != null && (
                    <>
                        {/* Filters Section - Only show when there's a search term */}
                        <YStack gap={8}>
                            <XStack
                                gap={12}
                                flexWrap="wrap"
                                alignItems="flex-start"
                            >
                                {/* Start Date Filter */}
                                <YStack
                                    minWidth={180}
                                    padding={8}
                                    borderRadius={6}
                                    backgroundColor="$white"
                                    gap={2}
                                >
                                    <BodySmallRegular
                                        color="$gray1000"
                                        fontWeight="emphasized"
                                        fontSize="$1"
                                    >
                                        Start Date
                                    </BodySmallRegular>
                                    <div style={{ width: '100%' }}>
                                        <style>
                                            {`
                                                #start-date .p-datepicker-trigger {
                                                    height: 32px !important;
                                                    width: 32px !important;
                                                }
                                                #start-date .p-inputtext {
                                                    height: 32px !important;
                                                }
                                            `}
                                        </style>
                                        <Calendar
                                            id="start-date"
                                            value={startDate}
                                            onChange={(e) => {
                                                setStartDate(
                                                    e.value as Date | null,
                                                );
                                            }}
                                            showIcon
                                            dateFormat="mm/dd/yy"
                                            placeholder="Select start date"
                                            maxDate={endDate ?? undefined}
                                            inputStyle={{
                                                padding: '6px 6px 6px 10px',
                                                borderRadius: 4,
                                                borderWidth: 1,
                                                borderColor: '#e0e0e0',
                                                fontSize: 12,
                                                width: '100%',
                                                height: '32px',
                                            }}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </YStack>

                                {/* End Date Filter */}
                                <YStack
                                    minWidth={180}
                                    padding={8}
                                    borderRadius={6}
                                    backgroundColor="$white"
                                    gap={2}
                                >
                                    <BodySmallRegular
                                        color="$gray1000"
                                        fontWeight="emphasized"
                                        fontSize="$1"
                                    >
                                        End Date
                                    </BodySmallRegular>
                                    <div style={{ width: '100%' }}>
                                        <style>
                                            {`
                                                #end-date .p-datepicker-trigger {
                                                    height: 32px !important;
                                                    width: 32px !important;
                                                }
                                                #end-date .p-inputtext {
                                                    height: 32px !important;
                                                }
                                            `}
                                        </style>
                                        <Calendar
                                            id="end-date"
                                            value={endDate}
                                            onChange={(e) => {
                                                setEndDate(
                                                    e.value as Date | null,
                                                );
                                            }}
                                            showIcon
                                            dateFormat="mm/dd/yy"
                                            placeholder="Select end date"
                                            minDate={startDate ?? undefined}
                                            inputStyle={{
                                                padding: '6px 6px 6px 10px',
                                                borderRadius: 4,
                                                borderWidth: 1,
                                                borderColor: '#e0e0e0',
                                                fontSize: 12,
                                                height: '32px',
                                            }}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </YStack>

                                {/* Sort By */}
                                <YStack
                                    minWidth={180}
                                    padding={8}
                                    borderRadius={6}
                                    backgroundColor="$white"
                                    gap={2}
                                >
                                    <BodySmallRegular
                                        color="$gray1000"
                                        fontWeight="emphasized"
                                        fontSize="$1"
                                    >
                                        Sort By
                                    </BodySmallRegular>
                                    <select
                                        id="sort-by"
                                        value={sortBy}
                                        onChange={(e) => {
                                            setSortBy(
                                                e.target.value as SortOption,
                                            );
                                        }}
                                        style={{
                                            padding: 6,
                                            borderRadius: 4,
                                            borderWidth: 1,
                                            borderColor: '#e0e0e0',
                                            fontSize: 12,
                                            width: '100%',
                                            backgroundColor: 'white',
                                            height: '32px',
                                        }}
                                    >
                                        <option value="relevance">
                                            Most Relevant
                                        </option>
                                        <option value="date-newest">
                                            Date (Newest First)
                                        </option>
                                        <option value="date-oldest">
                                            Date (Oldest First)
                                        </option>
                                        <option value="amount">Amount</option>
                                    </select>
                                </YStack>
                            </XStack>

                            {/* Clear Button */}
                            {(hasActiveFilters ||
                                debouncedQuery.length > 0) && (
                                <XStack>
                                    <PillButton
                                        text="Clear filters"
                                        onPress={handleClearFilters}
                                        variant="tertiary"
                                    />
                                </XStack>
                            )}
                        </YStack>

                        <TransactionSearchResults
                            transactions={searchResponse.results}
                            query={searchResponse.query}
                            accounts={allAccounts}
                            sortBy={sortBy}
                            bills={membershipDetails.bills ?? []}
                        />
                    </>
                )}
            {debouncedQuery.length === 0 && (
                <BodyMediumRegular>
                    Enter a search query to find transactions
                </BodyMediumRegular>
            )}
        </YStack>
    );
};
